import {Breadcrumbs, Button, Caption, Placeholder, Timeline} from "@telegram-apps/telegram-ui";
import {Link} from "@/components/Link/Link.tsx";
import {useTranslation} from "react-i18next";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useOutletContext} from "react-router-dom";
import {AuthProfile, Profile, Wish} from "@/api/types.ts";
import {TbEye, TbEyeOff} from "react-icons/tb";
import {useEffect, useMemo, useState} from "react";
import {getProfileWishes} from "@/api/apiMethods.ts";
import {FullScreenLoader} from "@/components/loader/FullScreenLoader.tsx";
import {ErrorState} from "@/components/ErrorState.tsx";
import {GiftedWishPreview} from "@/components/Wish/GiftedWishPreview.tsx";

type GiftedWishGroup = {
  key: string;
  header: string;
  wishes: Wish[];
};

const GIFTED_WISHES_SCROLL_STORAGE_KEY = "giftedWishesScrollY";

type GiftedWishesScrollState = {
  wishId: string;
  scrollY: number;
  elementTop: number;
};

const parseGiftedDate = (date: Date | string | null | undefined) => {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
};

const getGiftedDateKey = (wish: Wish) => {
  const giftedAt = parseGiftedDate(wish.giftedAt);
  if (!giftedAt) {
    return "without-date";
  }

  return `${giftedAt.getFullYear()}-${giftedAt.getMonth()}-${giftedAt.getDate()}`;
};

const getGiftedDateHeader = (wish: Wish, language: string, withoutDateText: string) => {
  const giftedAt = parseGiftedDate(wish.giftedAt);
  if (!giftedAt) {
    return withoutDateText;
  }

  return giftedAt.toLocaleDateString(language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const readGiftedWishesScrollState = (): GiftedWishesScrollState | null => {
  const rawState = window.sessionStorage.getItem(GIFTED_WISHES_SCROLL_STORAGE_KEY);
  if (!rawState) {
    return null;
  }

  try {
    const state = JSON.parse(rawState) as GiftedWishesScrollState;
    if (!state.wishId || !Number.isFinite(state.scrollY) || !Number.isFinite(state.elementTop)) {
      return null;
    }
    return state;
  } catch {
    return null;
  }
};

export const GiftedWishesPage = () => {
  const {t, i18n: {language}} = useTranslation();
  const {navigateToRoute} = useNavigation();
  const context = useOutletContext<{authProfile?: AuthProfile; profile?: Profile}>();
  const [wishes, setWishes] = useState<Wish[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const isCurrentUserPage = Boolean(context.authProfile);
  const showGiftedWishes = context.authProfile?.user.showGiftedWishes ?? true;
  const profileRoute = context.profile ? `/profiles/${context.profile.user.id}` : "/main";
  const userId = context.authProfile?.user.id ?? context.profile?.user.id;

  useEffect(() => {
    if (!userId) {
      setIsError(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);
    getProfileWishes(userId, {status: "gifted", sortBy: "giftedAt"})
      .then((wishes) => {
        setWishes(wishes);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userId]);

  useEffect(() => {
    if (isLoading || isError || wishes.length === 0) {
      return;
    }

    const scrollState = readGiftedWishesScrollState();
    if (!scrollState) {
      return;
    }

    window.sessionStorage.removeItem(GIFTED_WISHES_SCROLL_STORAGE_KEY);
    const restoreScroll = () => {
      const wishElement = document.querySelector<HTMLElement>(`[data-gifted-wish-id="${scrollState.wishId}"]`);
      if (!wishElement) {
        window.scrollTo({top: scrollState.scrollY});
        return;
      }

      const targetScrollY = window.scrollY + wishElement.getBoundingClientRect().top - scrollState.elementTop;
      window.scrollTo({top: targetScrollY});
    };

    requestAnimationFrame(restoreScroll);
    const restoreTimeouts = [100, 300, 700].map((timeout) => {
      return window.setTimeout(restoreScroll, timeout);
    });

    return () => {
      restoreTimeouts.forEach(window.clearTimeout);
    };
  }, [isError, isLoading, wishes.length]);

  const saveGiftedWishesScrollState = (wish: Wish) => {
    const wishElement = document.querySelector<HTMLElement>(`[data-gifted-wish-id="${wish.id}"]`);
    window.sessionStorage.setItem(GIFTED_WISHES_SCROLL_STORAGE_KEY, JSON.stringify({
      wishId: wish.id,
      scrollY: window.scrollY,
      elementTop: wishElement?.getBoundingClientRect().top ?? 0,
    }));
  };

  const navigateToGiftedWishEdit = (wish: Wish) => {
    saveGiftedWishesScrollState(wish);
    navigateToRoute(`/main/wishes/gifted/${wish.id}`);
  };

  const giftedWishGroups = useMemo<GiftedWishGroup[]>(() => {
    return wishes.reduce<GiftedWishGroup[]>((groups, wish) => {
      const key = getGiftedDateKey(wish);
      const existingGroup = groups.find(group => group.key === key);
      if (existingGroup) {
        existingGroup.wishes.push(wish);
        return groups;
      }

      groups.push({
        key,
        header: getGiftedDateHeader(wish, language, t("wish.gifted.withoutDate")),
        wishes: [wish],
      });
      return groups;
    }, []);
  }, [language, wishes]);

  return (
    <div className="mr-3 ml-3 flex flex-col gap-3">
      {isCurrentUserPage && (
        <Breadcrumbs>
          <Breadcrumbs.Item><Link to={"/main"}>{t("navigation.main")}</Link></Breadcrumbs.Item>
          <Breadcrumbs.Item>
            {t("profile.giftedWishes")}
          </Breadcrumbs.Item>
        </Breadcrumbs>
      )}

      <div className="p-3 rounded-xl bg-section flex flex-col gap-2">
        <Caption className="text-color-hint">
          {t(
            isCurrentUserPage ? "profile.yourGiftedWishesDescription" : "profile.userGiftedWishesDescription",
            {name: context.profile?.user.fullname}
          )}
        </Caption>
        {isCurrentUserPage && (
          <Caption className="text-color-hint flex items-start gap-1">
            <span className="pt-0.5">
              {showGiftedWishes ? <TbEye size={16}/> : <TbEyeOff size={16}/>}
            </span>
            <span>
              {t(
                showGiftedWishes
                  ? "settings.giftedWishesVisibility.visibleDescription"
                  : "settings.giftedWishesVisibility.hiddenDescription"
              )}
              {" "}
              <span
                className="tg-link-color underline"
                role="link"
                tabIndex={0}
                onClick={() => navigateToRoute("/main/settings")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    navigateToRoute("/main/settings");
                  }
                }}
              >
                {t("settings.giftedWishesVisibility.settingsLink")}
              </span>
            </span>
          </Caption>
        )}
      </div>

      {isLoading && <FullScreenLoader/>}

      {!isLoading && isError && (
        <ErrorState title="" description={t("state.temporaryUnavailable")}/>
      )}

      {!isLoading && !isError && wishes.length > 0 && (
        <Timeline active={giftedWishGroups.length} className="gifted-wishes-timeline">
          {giftedWishGroups.map((group) => (
            <Timeline.Item key={group.key} header={group.header}>
              <div className="flex flex-col gap-3">
                {group.wishes.map((wish) => (
                  <div
                    key={"gifted_wish_" + wish.id}
                    className="flex flex-col"
                    data-gifted-wish-id={wish.id}
                  >
                    <GiftedWishPreview
                      wish={wish}
                      showGiftedBy={Boolean(wish.showGiftedBy)}
                      onEditClick={isCurrentUserPage ? () => navigateToGiftedWishEdit(wish) : undefined}
                      onNavigateAway={() => saveGiftedWishesScrollState(wish)}
                    />
                  </div>
                ))}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      )}

      {!isLoading && !isError && wishes.length === 0 && (
        <Placeholder
          header=""
          description={t("state.emptyGiftedWishes")}
          action={
            <Button mode="plain" onClick={() => navigateToRoute(profileRoute, true)}>
              {isCurrentUserPage ? t("navigation.toMain") : t("profile.titleOtherProfile")}
            </Button>
          }
        />
      )}
    </div>
  );
};
